import { Controller } from '@hotwired/stimulus'
import { Modal, Tooltip } from 'bootstrap'

export default class extends Controller {
  static targets = [
    'bulkUpdateButton',
    'serviceRecords',
    'bulkUpdateButtonError',
    'bulkDeleteButton',
    'bulkManagementActionsContainer',
    'bulkManagementActionsContainerSummary',
    'bulkManagementActionsContainerSummaryCount',
    'bulkManagementActionsContainerSummaryText',
    'exportSelectedButton',
    'exportButton'
  ]

  static outlets = ['ui--table']

  static values = {
    url: String,
    deleteUrl: String,
    exportModalUrl: String,
    exportUrl: String,
    currentPageRecords: Number,
    totalRecords: Number
  }

  connect() {
    this.selection = []
    if (this.hasBulkUpdateButtonTarget) {
      this.defaultBulkEditButtonLabel = this.bulkUpdateButtonTarget.text
    }
    if (this.hasBulkDeleteButtonTarget) {
      this.defaultBulkDeleteButtonLabel = this.bulkDeleteButtonTarget.text
    }
    this.liveRegion = document.getElementById('live-region-polite')
  }

  uiTableOutletConnected(outlet) {
    this.selection = outlet.selectionData
    outlet.addSelectionChangeListener((event) => {
      this.selection = event.detail.selection
      this.refreshBulkActionButtons()
    })
    this.refreshBulkActionButtons()
  }

  // Legacy DOM-driven selection for pages that haven't been migrated to UI::Table::ConfigComponent
  // (e.g. providers/statements/service_records). The migrated pages use the ui--table outlet above.
  toggleCheckBoxes() {
    if (!this.hasServiceRecordsTarget) return

    this.serviceRecordSelected = !this.serviceRecordSelected
    const checkboxes = this.serviceRecordsTarget.querySelectorAll('input[type="checkbox"]')
    checkboxes.forEach((el) => (el.checked = this.serviceRecordSelected))

    this.toggleBulkActionButtons()
  }

  toggleBulkActionButtons() {
    if (!this.hasServiceRecordsTarget) return

    const selectedServiceRecords = Array.from(
      this.serviceRecordsTarget.querySelectorAll('input.service-record-checkbox:checked')
    )

    this.selection = selectedServiceRecords.map((checkbox) => ({
      id: parseInt(checkbox.value, 10),
      program: checkbox.parentElement?.parentElement?.dataset?.program
    }))

    this.refreshBulkActionButtons()
  }

  // reveals or hides buttons to bulk edit / delete selected service records based on current selection
  refreshBulkActionButtons() {
    const selectedCount = this.selection.length

    if (selectedCount > 0) {
      const firstProgram = this.selection[0].program
      const allMatchingProgram = this.selection.every((record) => record.program === firstProgram)
      const tooManyRecords = selectedCount > 250

      if (allMatchingProgram && !tooManyRecords) {
        if (this.hasBulkUpdateButtonErrorTarget) {
          this.hide(this.bulkUpdateButtonErrorTarget.parentElement)
        }
        if (this.hasBulkUpdateButtonTarget) {
          this.reveal(this.bulkUpdateButtonTarget)
          this.setSummaryCountandTextFor(selectedCount)
          this.reveal(this.bulkManagementActionsContainerSummaryTarget)
        }
      } else {
        if (this.hasBulkUpdateButtonTarget) {
          this.hide(this.bulkUpdateButtonTarget)
        }
        if (this.hasBulkUpdateButtonErrorTarget) {
          let tooltipMessage
          if (tooManyRecords) {
            tooltipMessage = 'Bulk editing is disabled when more than 250 records are selected. Please reduce to 250 or fewer to enable bulk editing.'
          } else {
            tooltipMessage = 'Filter your service records down to a single program to enable editing'
          }

          const tooltipWrapper = this.bulkUpdateButtonErrorTarget.parentElement

          // Update the data-bs-title attribute (used by Bootstrap tooltips)
          tooltipWrapper.setAttribute('data-bs-title', tooltipMessage)
          tooltipWrapper.setAttribute('data-bs-original-title', tooltipMessage)

          // Update the Bootstrap tooltip instance if it exists
          const tooltipInstance = Tooltip.getInstance(tooltipWrapper)
          if (tooltipInstance) {
            // Dispose and recreate the tooltip with the new title
            tooltipInstance.dispose()
            new Tooltip(tooltipWrapper, {
              title: tooltipMessage
            })
          }

          this.reveal(this.bulkUpdateButtonErrorTarget.parentElement)
          this.setSummaryCountandTextFor(selectedCount)
          this.reveal(this.bulkManagementActionsContainerSummaryTarget)
        }
      }
      if (this.hasBulkDeleteButtonTarget) {
        this.reveal(this.bulkDeleteButtonTarget)
        this.setSummaryCountandTextFor(selectedCount)
      }

      if (this.hasBulkManagementActionsContainerTarget) {
        this.reveal(this.bulkManagementActionsContainerTarget)
      }
    } else {
      this.liveRegion.textContent = 'No service records are selected'
      if (this.hasBulkUpdateButtonTarget) {
        this.hide(this.bulkUpdateButtonTarget)
      }

      if (this.hasBulkDeleteButtonTarget) {
        this.hide(this.bulkDeleteButtonTarget)
      }

      if (this.hasBulkUpdateButtonErrorTarget) {
        this.hide(this.bulkUpdateButtonErrorTarget.parentElement)
      }

      if (this.hasBulkManagementActionsContainerTarget) {
        this.hide(this.bulkManagementActionsContainerTarget)
      }
    }
  }

  reveal(element) {
    element.classList.remove('d-none')
  }

  hide(element) {
    element.classList.add('d-none')
  }

  setSummaryCountandTextFor = (selectedCount) => {
    this.bulkManagementActionsContainerSummaryCountTarget.innerHTML = selectedCount
    const followText = selectedCount === 1 ? 'service record on this page is selected' : 'service records on this page are selected'
    this.bulkManagementActionsContainerSummaryTextTarget.innerHTML = `&nbsp;${followText}`
    this.liveRegion.textContent = selectedCount + followText
  }

  // sends request which will pop a modal for bulk updating fields on selected service records
  updateSelectedServiceRecords(event) {
    event.preventDefault()

    this.openModalWithSelectedServiceRecord(this.urlValue)
  }

  // sends request which will pop a modal for bulk deleting selected service records
  deleteSelectedServiceRecords(event) {
    event.preventDefault()

    this.openModalWithSelectedServiceRecord(this.deleteUrlValue)
  }

  exportServiceRecords(event) {
    event.preventDefault()

    this.openModalWithSelectedServiceRecord(this.exportModalUrlValue, false)
  }

  exportSelectedServiceRecords(event) {
    event.preventDefault()

    this.openModalWithSelectedServiceRecord(this.exportModalUrlValue)
  }

  openModalWithSelectedServiceRecord(modalUrl, selectionWarning = true) {
    const selectedIds = this.selection.map((record) => record.id)

    if (selectedIds.length > 0 || !selectionWarning) {
      var baseURL = new URL(modalUrl)
      var searchParams = new URLSearchParams(baseURL.search)

      if (selectedIds.length > 0) {
        selectedIds.forEach((id) => searchParams.append('ids[]', id))
        searchParams.append('selected_count', selectedIds.length)
      }

      searchParams.append('current_page_count', this.currentPageRecordsValue)
      searchParams.append('total_count', this.totalRecordsValue)

      const url = baseURL.pathname + '?' + searchParams

      if (url.length >= 10240) {
        alert(`Too many service records selected.`)
      } else {
        document.getElementById('modal').src = url
      }
    } else {
      document.dispatchEvent(
        new CustomEvent('toast', { detail: { failure: true, message: 'No service records selected' } })
      )
    }
  }

  export(event) {
    event.preventDefault()

    let exportTypeRadioButton = document.querySelector('input[name="export_type"]:checked')
    let exportType = exportTypeRadioButton ? exportTypeRadioButton.value : null

    if (exportType) {
      var baseURL = new URL(this.exportUrlValue)
      var searchParams = new URLSearchParams(baseURL.search)

      searchParams.append('export_type', exportType)

      this.downloadCSV(baseURL, searchParams)
    } else {
      alert('Please specify which rows you want to export.')
    }
  }

  downloadCSV(baseURL, searchParams) {
    const url = baseURL.pathname + '?' + searchParams

    let csvDownloadLink = document.getElementById('download-csv-link')
    csvDownloadLink.href = url
    csvDownloadLink.click()

    Modal.getOrCreateInstance('#modal').hide()
  }
}
